import {
  createFormState,
  hasLength,
  includedIn,
  isProbablyEmail,
  isAmericanOrCanadianRoutingNumber,
  numberGreaterThan,
  required,
  validateWhen,
  onlyIntegers,
  numberGreaterThanOrEqualTo,
} from 'redux-freeform';
import { CHECK_TYPE } from '../../utils/constants';

export const DEFAULT_PAYMENT_VALUE = '';

const creditCardFormConfig = {
  amount: {
    defaultValue: DEFAULT_PAYMENT_VALUE,
    validators: [required(), numberGreaterThan(0)],
  },
  submission_type: {
    defaultValue: 'Card reader',
    validators: [],
  },
  first_name: {
    defaultValue: '',
    validators: [], // 'required' validator is conditionally added based on: payer_name_required or submission_type
  },
  last_name: {
    defaultValue: '',
    validators: [], // 'required' validator is conditionally added based on payer_name_required or submission_type
  },
  number: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        hasLength(13, 16),
        includedIn(['Manual entry']),
        'submission_type',
      ),
      validateWhen(
        onlyIntegers(),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
  month: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        hasLength(2, 2),
        includedIn(['Manual entry']),
        'submission_type',
      ),
      validateWhen(
        onlyIntegers(),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
  year: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        hasLength(4, 4),
        includedIn(['Manual entry']),
        'submission_type',
      ),
      validateWhen(
        onlyIntegers(),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
  cvv: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        hasLength(3, 4),
        includedIn(['Manual entry']),
        'submission_type',
      ),
      validateWhen(
        onlyIntegers(),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
  zipcode: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        numberGreaterThanOrEqualTo(5),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
};

const cashFormConfig = {
  amount: {
    defaultValue: DEFAULT_PAYMENT_VALUE,
    validators: [required(), numberGreaterThan(0)], // 'numberGreaterThan' validator is conditionally modified based on split_tender_allowed
  },
  first_name: {
    defaultValue: '',
    validators: [], // 'required' validator is conditionally added based on payer_name_required
  },
  last_name: {
    defaultValue: '',
    validators: [], // 'required' validator is conditionally added based on payer_name_required
  },
  payment_delivery_type: {
    defaultValue: 'in_person',
    validators: [required()],
  },
};

const checkFormConfig = {
  amount: {
    defaultValue: DEFAULT_PAYMENT_VALUE,
    validators: [required(), numberGreaterThan(0)],
  },
  first_name: {
    defaultValue: '',
    validators: [required()],
  },
  last_name: {
    defaultValue: '',
    validators: [required()],
  },
  check_type: {
    defaultValue: CHECK_TYPE.PERSONAL,
    validators: [required()],
  },
  account_type: {
    defaultValue: 'Checking',
    validators: [
      validateWhen(required(), includedIn('Manual entry', 'submission_type')),
    ],
  },
  submission_type: {
    defaultValue: 'Check reader',
    validators: [required()],
  },
  company_name: {
    defaultValue: '',
    validators: [],
  },
  check_number: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        hasLength(3, 15),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
  routing_number: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
      validateWhen(
        isAmericanOrCanadianRoutingNumber(),
        includedIn(['Manual entry']),
        'submission_type',
      ),
    ],
  },
  account_number: {
    defaultValue: '',
    validators: [
      validateWhen(required(), includedIn(['Manual entry']), 'submission_type'),
    ],
  },
};

const wireFormConfig = {
  amount: {
    defaultValue: DEFAULT_PAYMENT_VALUE,
    validators: [required(), numberGreaterThan(0)],
  },
  name: {
    defaultValue: '',
    validators: [required()],
  },
  effectiveDate: {
    defaultValue: '',
    validators: [required()],
  },
  description: {
    defaultValue: '',
    validators: [],
  },
};

const paylinkFormConfig = {
  amount: {
    defaultValue: DEFAULT_PAYMENT_VALUE,
    validators: [required(), numberGreaterThan(0)],
  },
  emailPayLinkCheckbox: {},
  emailAddress: {
    validators: [
      // includedIn[true] will only apply validation if the related checkbox field is checked (true)
      validateWhen(required(), includedIn([true]), 'emailPayLinkCheckbox'),
      validateWhen(
        isProbablyEmail(),
        includedIn([true]),
        'emailPayLinkCheckbox',
      ),
    ],
  },
  textPayLinkCheckbox: {},
  phoneNumber: {
    validators: [
      // includedIn[true] will only apply validation if the related checkbox field is checked (true)
      validateWhen(required(), includedIn([true]), 'textPayLinkCheckbox'),
      validateWhen(
        hasLength(10, 10),
        includedIn([true]),
        'textPayLinkCheckbox',
      ),
    ],
    constraints: [hasLength(0, 10), onlyIntegers()],
  },
};

export const {
  reducer: cardReducer,
  mapStateToProps: cardMapStateToProps,
  mapDispatchToProps: cardMapDispatchToProps,
} = createFormState(creditCardFormConfig);

export const {
  reducer: cashReducer,
  mapStateToProps: cashMapStateToProps,
  mapDispatchToProps: cashMapDispatchToProps,
} = createFormState(cashFormConfig);

export const {
  reducer: checkReducer,
  mapStateToProps: checkMapStateToProps,
  mapDispatchToProps: checkMapDispatchToProps,
} = createFormState(checkFormConfig);

export const {
  reducer: wireReducer,
  mapStateToProps: wireMapStateToProps,
  mapDispatchToProps: wireMapDispatchToProps,
} = createFormState(wireFormConfig);

export const {
  reducer: paylinkReducer,
  mapStateToProps: paylinkMapStateToProps,
  mapDispatchToProps: paylinkMapDispatchToProps,
} = createFormState(paylinkFormConfig);
